import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Button, Typography } from '@mui/material';
import parse, { domToReact } from 'html-react-parser';
import React, { useEffect, useMemo, FC } from 'react';

import { getStyleObjectFromString } from '../../theme/theme';
import BoatList from '../common/BoatList';
import PropertyList from '../common/PropertyList';

type ComponentEntry = {
  name: string;
  component: FC<any>;
};

type ComponentKey = 'boatlist' | 'propertylist';

const Components: Record<ComponentKey, ComponentEntry> = {
  boatlist: { name: 'BoatList', component: BoatList },
  propertylist: { name: 'PropertyList', component: PropertyList },
};

interface OwnProps {
  content?: string;
  trimLines?: number;
}

// Strips tags and decodes entities so the {{boatlist}}/{{propertylist}}
// shortcode regex below can match content regardless of surrounding markup.
// `DOMParser` doesn't exist during SSR (Vike/Node) — this runs synchronously
// in a `useMemo` in the render path, not an effect, so it needs a
// server-safe fallback rather than crashing.
const htmlDecode = (input: string) => {
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
  }
  return input
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&nbsp;/g, ' ');
};

const ContentRender = (props: OwnProps) => {
  const { content = '', trimLines = 0 } = props;
  const [showMore, setShowMore] = React.useState(false);
  const [exceedsTrimLines, setExceedsTrimLines] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const contentBody = useMemo(() => {
    const re = /{{(.+?)}}/g;
    let html = content;
    const matches = htmlDecode(content ?? '')?.match(re) ?? [];
    matches.forEach((match) => {
      const compNameMatch = match.match(/\w+/g);
      if (!compNameMatch || compNameMatch.length === 0) return;

      const compName = compNameMatch[0] as string;
      const params = match.match(/(\w+)=\s*(?:"([^"]*)"|(\w+))/gm) || [];

      // Type guard to check if compName is a valid ComponentKey
      const isValidComponent = (key: string): key is ComponentKey =>
        Object.keys(Components).includes(key);

      if (isValidComponent(compName)) {
        const compOutput = `<${Components[compName].name} ${params.join(' ')}/>`;
        const ce = new RegExp(`{{${compName}.+?}}`);
        html = html.replace(ce, compOutput);
      }
    });

    const options = {
      replace: (domNode: any) => {
        // Type guard to check if domNode.name is a valid ComponentKey
        const isValidComponent = (key: string): key is ComponentKey =>
          Object.keys(Components).includes(key);

        if (
          domNode.name &&
          typeof domNode.name === 'string' &&
          isValidComponent(domNode.name)
        ) {
          const componentEntry =
            Components[domNode.name as keyof typeof Components];
          return React.createElement(componentEntry.component, domNode.attribs);
        } else if (
          domNode.name &&
          ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'li'].includes(domNode.name)
        ) {
          let variant = domNode.name;
          if (variant === 'p') {
            variant = 'body1';
          }
          try {
            // Ensure attribs is always an object
            const attribs =
              domNode.name === 'p' || domNode.name === 'li'
                ? (domNode?.attribs ?? {})
                : {};
            const styleStr = attribs.style ?? '';
            const classes = attribs.class ?? '';
            if (domNode.name === 'li') {
              return (
                <Typography
                  component="li"
                  className={`list-item ${classes}`}
                  style={{
                    ...getStyleObjectFromString(styleStr),
                    fontFamily: 'Roboto',
                  }}
                >
                  {domToReact(domNode.children, options)}
                </Typography>
              );
            } else {
              return (
                <Typography
                  className={`${classes} ${domNode.name === 'li' ? 'list-item' : ''}`}
                  variant={variant}
                  paragraph={true}
                  style={{
                    ...getStyleObjectFromString(styleStr),
                    fontFamily: 'Roboto',
                  }}
                >
                  {domToReact(domNode.children, options)}
                </Typography>
              );
            }
          } catch (e) {
            console.log(e);
            return <div>something bad happened.</div>;
          }
        }
      },
    };
    return parse(html, options);
  }, [content]);

  useEffect(() => {
    if (trimLines && contentRef.current) {
      const lineHeight = parseInt(
        window
          .getComputedStyle(contentRef.current)
          .getPropertyValue('line-height'),
        10
      );
      const maxHeight = trimLines * lineHeight;
      if (contentRef.current.scrollHeight > maxHeight) {
        setExceedsTrimLines(true);
      } else {
        setExceedsTrimLines(false);
      }
    }
  }, [content, trimLines]);

  return (
    <div ref={contentRef} style={{ lineHeight: '1.2' }}>
      {trimLines ? (
        <React.Fragment>
          {showMore ? (
            contentBody
          ) : (
            <div
              style={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitLineClamp: trimLines,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {contentBody}
            </div>
          )}

          {exceedsTrimLines && (
            <Button
              onClick={() => setShowMore(!showMore)}
              endIcon={showMore ? <ExpandLess /> : <ExpandMore />}
              sx={{ padding: '10px 0' }}
            >
              {showMore ? 'Show Less' : 'Show More'}
            </Button>
          )}
        </React.Fragment>
      ) : (
        <>{contentBody}</>
      )}
    </div>
  );
};

export default ContentRender;
